import { format, formatDistance } from 'date-fns';
import type {
  Agent,
  Artist,
  Booking,
  CustomerRecord,
  RebookAttribution,
  RebookDeclineReason,
  RebookOutcome,
  StudioId,
} from '@/domain/types';
import { useSimulationStore } from '@/state/simulationStore';
import { useSessionStore } from '@/state/sessionStore';
import { useBrainStore } from '@/state/brainStore';

export const phone = (raw: string): string => raw;

/**
 * Lift the first AU phone number out of free text (mobiles and common landline
 * forms), returned as written. Deliberately a plain regex, not AI - the manual
 * placeholder for a later AI fill used by the "Push to agent queue" popover. Returns
 * undefined when no number is present.
 */
export function extractAuPhone(text: string): string | undefined {
  // Leading +61 / 61 / 0, then a mobile (4xx xxx xxx) or landline ([2378]xxx xxxx),
  // with optional space or dash separators.
  const re =
    /(?:\+?61[\s-]?|0)(?:4\d{2}[\s-]?\d{3}[\s-]?\d{3}|[2378][\s-]?\d{4}[\s-]?\d{4})/;
  const match = text.match(re);
  return match ? match[0].trim() : undefined;
}

/**
 * Detect a "call me at 3pm"-style request in free text and resolve it to an ISO
 * instant on the current sim day (passed nowIso, never Date.now()). Requires an
 * am/pm marker to keep false positives down. Returns undefined when none is found.
 */
export function extractCallbackTime(text: string, nowIso: string): string | undefined {
  const match = text.match(/(\d{1,2})(?::(\d{2}))?\s*(am|pm)/i);
  if (!match) return undefined;
  let hour = Number.parseInt(match[1]!, 10);
  const mins = match[2] ? Number.parseInt(match[2], 10) : 0;
  const meridiem = match[3]!.toLowerCase();
  if (hour < 1 || hour > 12 || mins > 59) return undefined;
  if (meridiem === 'pm' && hour !== 12) hour += 12;
  if (meridiem === 'am' && hour === 12) hour = 0;
  const when = new Date(nowIso);
  when.setHours(hour, mins, 0, 0);
  return when.toISOString();
}

export const simTimeShort = (iso: string): string =>
  format(new Date(iso), 'HH:mm', { weekStartsOn: 1 });

export const simTimeFull = (iso: string): string => format(new Date(iso), 'EEE d MMM, HH:mm');

// Compact day+month, e.g. "3 Mar". Used for terse labels like the reference
// picker's "used on …" badge.
export const simDateShort = (iso: string): string => format(new Date(iso), 'd MMM');

/**
 * A session length in hours (bgg4 #4): whole hours read "2h", a part hour keeps
 * one decimal ("1.5h"), so a 90-minute sitting never rounds up to "2h". The one
 * formatter for every session-hours label.
 */
export const hoursLabel = (mins: number): string => {
  const h = mins / 60;
  return Number.isInteger(h) ? `${h}h` : `${Number(h.toFixed(1))}h`;
};

// Relative time anchored against the simulation clock, not the real-world clock.
// Without this, fixture timestamps in May 2026 render as "9 days ago" against real-world May 13.
/** Relative to the sim clock, or to `nowIso` when given (e.g. wall time for a real HookChat DM). */
export const relativeFromIso = (iso: string, nowIso?: string): string => {
  const simNow = new Date(nowIso ?? useSimulationStore.getState().clockIso);
  const target = new Date(iso);
  return target.getTime() <= simNow.getTime()
    ? `${formatDistance(target, simNow)} ago`
    : `in ${formatDistance(target, simNow)}`;
};

function canViewFinancials(): boolean {
  const persona = useSessionStore.getState().persona;
  if (!persona) return true; // no persona (e.g. selector screen) → show money
  const row = useBrainStore.getState().rolePermissions.find((r) => r.personaKey === persona);
  // Permissive default: if a persona has no permissions row, show money rather
  // than hide it. 'view' and 'edit' both reveal; only 'none' masks.
  return !row || row.capabilities.financials !== 'none';
}

/**
 * AUD, never masked. Use it for text a CUSTOMER receives (a deposit message sent
 * on their channel): the staff persona's `financials` mask must not reach them.
 * On-screen amounts use `aud()`.
 */
export const formatAud = (amount: number): string =>
  new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
  }).format(amount);

export const aud = (amount: number): string => {
  // Money visibility is role-based: masked unless the active persona's `financials`
  // permission allows it. Read at call time → applies on the next render as the
  // user navigates or switches persona.
  if (!canViewFinancials()) return '$••••';
  return formatAud(amount);
};

export const customerName = (c: Pick<CustomerRecord, 'firstName' | 'lastName' | 'preferredName'>) =>
  c.preferredName ?? `${c.firstName} ${c.lastName}`;

/**
 * Human placement label from the structured body-part fields, e.g.
 * "Right inner forearm". Falls back to the legacy free-text `placement` when no
 * structured `bodyPart` is set, so the design section always has something to show.
 */
export function formatBodyPart(
  booking: Pick<Booking, 'bodyPart' | 'bodyPartSide' | 'bodyPartQualifier' | 'placement'>,
): string {
  if (!booking.bodyPart) return booking.placement ?? '';
  const words = [booking.bodyPartSide, booking.bodyPartQualifier, booking.bodyPart]
    .filter(Boolean)
    .map((w) => (w as string).replace(/-/g, ' '));
  const joined = words.join(' ');
  return joined.charAt(0).toUpperCase() + joined.slice(1);
}

export const studioDisplay = (id: StudioId): string =>
  id
    .split('-')
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(' ');

export const studioLabel = (s: { brand: string; name: string }): string =>
  `${s.brand} · ${s.name}`;

const DECLINE_REASON_LABEL: Record<RebookDeclineReason, string> = {
  'not-sure-on-design': 'Not sure on design',
  'going-overseas': 'Going overseas',
  'moving-studios': 'Moving studios',
  'needs-to-think': 'Needs to think',
  other: 'Other',
};

/**
 * Renders a short label + tone class hint for a captured rebook outcome.
 * Looks up the facilitator's display name from the supplied artists/agents
 * lookups so the chip can show "attributed to Mia" rather than a raw id.
 */
export function formatRebookOutcome(
  outcome: RebookOutcome,
  attribution: RebookAttribution | undefined,
  artists: Record<string, Artist>,
  agents: Record<string, Agent>,
): { label: string; tone: 'mint' | 'blood' | 'gold' } {
  const staffId = attribution?.facilitatedByStaffId;
  const staffName = (() => {
    if (!staffId) return undefined;
    if (artists[staffId]) return artists[staffId].displayName || artists[staffId].firstName;
    if (agents[staffId]) return agents[staffId].firstName;
    if (staffId === 'sales-team') return 'sales team';
    return undefined;
  })();
  if (outcome.kind === 'rebookedInChair') {
    return {
      label: staffName ? `Rebooked · ${staffName}` : 'Rebooked in chair',
      tone: 'mint',
    };
  }
  if (outcome.kind === 'willRebookLater') {
    return {
      label: staffName ? `Will rebook · ${staffName}` : 'Will rebook · book later',
      tone: 'mint',
    };
  }
  if (outcome.kind === 'noRebookDepositApplied') {
    // L3: reason is now free text; keep pretty labels for the old coded reasons.
    const coded = (DECLINE_REASON_LABEL as Record<string, string>)[outcome.reason];
    return {
      label: `Declined · ${coded ?? outcome.reason}`,
      tone: 'blood',
    };
  }
  if (outcome.kind === 'pieceComplete') {
    return { label: 'Piece complete', tone: 'mint' };
  }
  // rebookDeferredToSales
  const resolved = outcome.resolvedOutcome;
  const tail = resolved === 'booked'
    ? 'booked'
    : resolved === 'declined'
      ? 'declined'
      : resolved === 'noContact'
        ? 'no contact'
        : 'pending';
  return {
    label: `Sales callback · ${tail}`,
    tone: 'gold',
  };
}
